import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Method } from '@core/enums/api.enum';
import { IApiRequest } from '@core/interfaces/api-request';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface MemoryCachedEntry<T = unknown> {
  time: Date;
  value: T;
}
const cache: { [key: string]: MemoryCachedEntry } = {};

interface LocalStorageCacheEntry<T = unknown> {
  time: number;
  value: T;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _http = inject(HttpClient);
  private _baseUrl = environment.APIBaseUrl;

  private _baseHttpHeaders = {};

  // Track in-flight refresh operations to avoid duplicate background requests
  private inFlightRefresh: Record<string, boolean> = {};

  request<T>(options: IApiRequest): Observable<T> {
    switch (options.method) {
      case Method.GET:
        return this._http
          .get<T>(`${this._baseUrl}${options.relativeUrl}`, {
            headers: this.getHeaders(options.headers),
            params: this.getParams(options.parameters),
            withCredentials: true,
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.POST:
        return this._http
          .post<T>(`${this._baseUrl}${options.relativeUrl}`, options.body, {
            headers: this.getHeaders(options.headers),
            withCredentials: true,
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.DELETE:
        return this._http
          .request<T>(Method.DELETE, `${this._baseUrl}${options.relativeUrl}`, {
            headers: this.getHeaders(options.headers),
            params: this.getParams(options.parameters),
            withCredentials: true,
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.PUT:
        return this._http
          .put<T>(`${this._baseUrl}${options.relativeUrl}`, options.body, {
            headers: this.getHeaders(options.headers),
            withCredentials: true,
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.PATCH:
        return this._http
          .patch<T>(`${this._baseUrl}${options.relativeUrl}`, options.body, {
            headers: this.getHeaders(options.headers),
            withCredentials: true,
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
    }
  }

  public requestCached<T>(options: IApiRequest, ttl = 60000): Observable<T> {
    const key = JSON.stringify(options);
    const now = new Date();
    if (cache[key] && now.getTime() - cache[key].time.getTime() < ttl) {
      return of(cache[key].value as T);
    }

    return this.request<T>(options).pipe(
      tap((res) => {
        cache[key] = { time: new Date(), value: res };
      })
    );
  }

  // LocalStorage key prefix to avoid clashes
  private readonly lsPrefix = 'api-cache:';

  /**
   * Stale-while-revalidate localStorage cached request.
   * Behaviour:
   *  - If no cached value: perform network request, store, return network observable
   *  - If cached and fresh (age < ttl): return cached value (as Observable) immediately
   *  - If cached but stale: return cached value immediately AND trigger background refresh
   *  - If background refresh succeeds it updates the cache silently (no emission to current subscriber)
   * Safety: JSON parse guarded, corrupt entries removed.
   */
  public requestLocalStorageCached<T>(
    options: IApiRequest,
    ttl = 5 * 60_000
  ): Observable<T> {
    const storageKey = this.lsPrefix + this.buildLocalStorageKey(options);
    const now = Date.now();

    const cached = this.safeReadLocalStorage<T>(storageKey);

    if (!cached) {
      return this.request<T>(options).pipe(
        tap((value) =>
          this.safeWriteLocalStorage<T>(storageKey, { time: now, value })
        )
      );
    }

    const age = now - cached.time;

    if (age < ttl) {
      // Fresh
      return of(cached.value);
    }

    // Stale: emit stale immediately, then refresh in background
    this.backgroundRefresh<T>(storageKey, options);
    return of(cached.value);
  }

  private backgroundRefresh<T>(storageKey: string, options: IApiRequest) {
    if (this.inFlightRefresh[storageKey]) return; // already refreshing
    this.inFlightRefresh[storageKey] = true;
    this.request<T>(options).subscribe({
      next: (value) =>
        this.safeWriteLocalStorage<T>(storageKey, { time: Date.now(), value }),
      error: () => {
        /* keep stale value on error */
      },
      complete: () => {
        delete this.inFlightRefresh[storageKey];
      },
    });
  }

  private buildLocalStorageKey(options: IApiRequest): string {
    const keyObj: Record<string, unknown> = {
      method: options.method,
      relativeUrl: options.relativeUrl,
      parameters: options.parameters || null,
      body: options.body || null,
    };
    return btoa(unescape(encodeURIComponent(JSON.stringify(keyObj))));
  }

  private safeReadLocalStorage<T>(
    key: string
  ): LocalStorageCacheEntry<T> | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as LocalStorageCacheEntry<T>;
    } catch (err) {
      try {
        localStorage.removeItem(key);
      } catch (innerErr) {
        /* ignore secondary errors */
      }
      return null;
    }
  }

  private safeWriteLocalStorage<T>(
    key: string,
    value: LocalStorageCacheEntry<T>
  ): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      // Possible quota exceeded; attempt cleanup then retry once.
      this.tryCleanupOldestEntries();
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (innerErr) {
        /* give up */
      }
    }
  }

  private tryCleanupOldestEntries(): void {
    try {
      const entries: { k: string; t: number }[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(this.lsPrefix)) continue;
        const item = localStorage.getItem(k);
        if (!item) continue;
        try {
          const parsed = JSON.parse(item) as { time?: number };
          if (parsed && typeof parsed.time === 'number')
            entries.push({ k, t: parsed.time });
        } catch {
          /* ignore parsing error */
        }
      }
      entries.sort((a, b) => a.t - b.t);
      const removeCount = Math.ceil(entries.length * 0.2);
      for (let i = 0; i < removeCount; i++) {
        localStorage.removeItem(entries[i].k);
      }
    } catch {
      /* ignore cleanup errors */
    }
  }

  private getHeaders(headers?: { [key: string]: string }): HttpHeaders {
    return new HttpHeaders({ ...this._baseHttpHeaders, ...(headers || {}) });
  }

  private getParams(params?: { [key: string]: any }): HttpParams {
    // eslint-disable-line @typescript-eslint/no-explicit-any
    let httpParams = new HttpParams();
    if (!params) return httpParams;
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        httpParams = httpParams.append(key, String(value));
      }
    }
    return httpParams;
  }

  private handleError(errorRes: HttpErrorResponse) {
    return throwError(() => errorRes);
  }
}
